'use client';

import React, { useEffect, useState } from "react";
import styles from "@/styles/menu.module.scss";
import { Button, Container } from "react-bootstrap";
import { useTranslations } from "next-intl";
import { Swiper, SwiperClass, SwiperSlide } from "swiper/react";
import "swiper/css";

import SearchBar from "@/components/search";
import CategoryCard, { Category } from "@/components/catelog/category-card";
import ItemCard from "@/components/catelog/item-card";
import Navbar from "@/components/navbar";
import FloatCart from "@/components/payments/cart-float";
import { ItemObj } from "../modals/order-details-sheet";

import useStore from "@store/index";
import PreloaderComponent from "@/components/preloader";

import emptyItemsLottie from "@/assets/lotties/empty_cart.json";
import Lottie from "lottie-react";
import Link from "next/link";
import { ArrowUp } from "react-feather";

export default function MenuComponent({ 
  categoryName, 
  categories, 
  fullCategories,
  pizzas 
}: { categoryName: string, categories: Category[], fullCategories: Category[], pizzas: ItemObj[] }) {
  
  const t = useTranslations();
  const { hasHydrated, setFullCategories } = useStore((state) => state);
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [categoryItems, setCategoryItemsList] = useState<Category[]>([]);
  const [showHint, setShowHint] = React.useState(true);
  const [swiperInstance, setSwiperInstance] = React.useState<SwiperClass | null>(null);

  useEffect(() => {
    if (fullCategories && fullCategories.length > 0) {
      setFullCategories(fullCategories);
    }
  }, [fullCategories, setFullCategories]);


  // useEffect(() => {
  //   if (hasHydrated) {
  //     const itemsByCategory: Category[] = [];

  //     pizzas.forEach((pizza: ItemObj) => {
  //       if (pizza.categories) {
  //         pizza.categories.forEach((category: Category) => {
  //           if (!itemsByCategory.some(item => item.categoryId === category.categoryId)) {
  //             itemsByCategory.push({
  //               categoryId: category.categoryId,
  //               categoryName: category.categoryName,
  //               categoryImageUrls: category.categoryImageUrls,
  //               subCategories: [],
  //               items: [],
  //               categoryType: category.categoryType
  //             });
  //           }

  //           const foundCategory = itemsByCategory.find(item => item.categoryId === category.categoryId);
  //           if (foundCategory) {
  //             foundCategory.items.push(pizza);
  //           }
  //         });
  //       }
  //     });

  //     setCategoryItemsList(itemsByCategory);
  //   }
  // }, [hasHydrated, categories, pizzas]);

  useEffect(() => {
    if (hasHydrated) {
      const itemsByCategory: Category[] = [];

      pizzas.forEach((pizza: ItemObj) => {
        if (pizza.categories) {
          pizza.categories.forEach((category: Category) => {
            if (!itemsByCategory.some(item => item.categoryId === category.categoryId )) {
                let categoryIndex = categories.findIndex(cat => cat.categoryId === category.categoryId);
                if (categoryIndex === -1) {
                  categoryIndex = categories.findIndex((cat) =>
                    Array.isArray(cat.subCategories) && cat.subCategories.some((sub: Category) => sub.categoryId === category.categoryId)
                  );
                }
                itemsByCategory.push({
                categoryId: category.categoryId,
                categoryName: category.categoryName,
                categoryImageUrls: category.categoryImageUrls,
                subCategories: [],
                items: [],
                categoryType: category.categoryType,
                parentIndex: categoryIndex !== -1 ? categoryIndex : undefined
                });
            }

            const foundCategory = itemsByCategory.find(item => item.categoryId === category.categoryId);
            if (foundCategory) {
              foundCategory.items.push(pizza);
            }
          });
        }
      });

      // If no categories were found but we have items, create a default category
      // if (itemsByCategory.length === 0 && pizzas.length > 0) {
      //   itemsByCategory.push({
      //     categoryId: 0,
      //     categoryName: categoryName || "Items",
      //     categoryImageUrls: [],
      //     subCategories: [],
      //     items: pizzas,
      //     categoryType: "default"
      //   });
      // }

      // Helper function to get sort index from fullCategories
      const getSortIndex = (categoryId: number): number => {
        const index = fullCategories.findIndex(cat => cat.categoryId === categoryId);
        if (index !== -1) return index;
        
        // Check if it's a subcategory and return parent's index with decimal offset
        for (let i = 0; i < fullCategories.length; i++) {
          if (Array.isArray(fullCategories[i].subCategories)) {
            const subIndex = fullCategories[i].subCategories.findIndex(sub => sub.categoryId === categoryId);
            if (subIndex !== -1) return i + (subIndex + 1) / 100;
          }
        }
        return Number.MAX_SAFE_INTEGER;
      };

      // Sort categories and subcategories by fullCategories order
      const sortedCategories = itemsByCategory.sort((a, b) => {
        const indexA = getSortIndex(a.categoryId);
        const indexB = getSortIndex(b.categoryId);
        
        return indexA - indexB;
      });

      setCategoryItemsList(sortedCategories);
    }
  }, [hasHydrated, categories, pizzas, fullCategories]);

  useEffect(() => {
    if(hasHydrated){
      const container = document.getElementById('main');
      const handleScroll = () => {
        if (container) {
          const scrollTop = container.scrollTop;
          setShowScrollTop(scrollTop > 300);
        }
      };
  
      container?.addEventListener('scroll', handleScroll);

      return () => container?.removeEventListener('scroll', handleScroll);
    }
  }, [hasHydrated]);

  const scrollToTop = () => {
    const container = document.getElementById('main');
    container?.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  if (!hasHydrated) {
    return <PreloaderComponent />; // Prevent rendering until store is hydrated
  }

  return (
    <>
      <Navbar pageTitle={categoryName} isBackButtonVisible={true} backButtonLink={"/"} />


      <main className={styles.main} id="main" >
        <Container className={styles.menuContainer} id="menu-container">

          {/* Search Bar */}
          <SearchBar />

          {/* Cart Float */}
          {/* <FloatCart /> */}

          {/* Page Title */}
          {(categoryItems && Object.keys(categoryItems).length === 0) || (
            <div className={styles.pageTitle}>
              <h1>{categoryName || t("MenuView.ALL_PIZZAS")}</h1>
            </div>
          )}
          {/* Categories */}
          <div className={styles.categories} style={{display: (categoryItems && categoryItems.length < 2) ?'none':'block'}}>
            <div className={styles.scrollHintWrapper}>
              <Swiper
                spaceBetween={0}
                slidesPerView={'auto'}
                navigation={false}
                pagination={{ clickable: true }}
                draggable={true}
                onReachEnd={() => setShowHint(false)}
                onFromEdge={() => setShowHint(true)}
                onSwiper={setSwiperInstance}
                className={styles.categoryCarousel}
              >
                {categoryItems && categoryItems.length > 0? 
                  categoryItems.map((subCategory: Category) => (
                    <SwiperSlide key={`${subCategory.categoryId}` } className={styles.categoryCarouselItem}>
                      <CategoryCard category={subCategory} isScrollCategory={true} />
                    </SwiperSlide>
                  ))
                : null}
              </Swiper>
              {showHint && (
                <div 
                  className={styles.scrollHintDiv} 
                  onClick={() => {
                    if (swiperInstance) {
                      swiperInstance.slideTo(swiperInstance.slides.length - 1);
                    }
                    setShowHint(false);
                  }}
                >
                  <div className={styles.scrollHint}>
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" className={styles.scrollHintIcon}>
                      <path d="M9 18l6-6-6-6" stroke="currentColor" strokeWidth="2" />
                    </svg>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* No Items Found */}
          {(!categoryItems || Object.keys(categoryItems).length === 0) && (
            <div className={styles.noItems}>
              <Lottie animationData={emptyItemsLottie} className={styles.emptyLottie} loop={true} />
              <h2>{t("MenuView.NOT_FOUND")}</h2>
              <p>{t("MenuView.NOT_FOUND_DESCRIPTION")}</p>
              <Link href="/" id="go-back-button">
                <Button variant="secondary" className={styles.button}>{t("ItemView.GO_BACK")}</Button>
              </Link>
            </div>
          )}

          {/* Pizza Lists */}
          {categoryItems && categoryItems.map((category, index) => (
            <div key={index} id={`category-${category.categoryId}`} className={styles.pizzaSection}>
              <h2 style={{display: (categoryItems && categoryItems.length < 2) ?'none':'block'}}>{category.categoryName}</h2>
              <div className={styles.pizzaGrid}>
                {category.items.map((pizza: ItemObj, index: number) => (
                  <React.Fragment key={index}>
                    <ItemCard pizza={pizza} />
                  </React.Fragment>
                ))}
              </div>
            </div>
          ))}

        </Container>

        <div className={styles.menuBottomStyles}>
          {/* Cart Float */}
          <div className={styles.menuFloatCart}>
            <FloatCart />
          </div>
          
          {/* Scroll to Top Button  */}
          <button 
            className={`${styles.scrollTopButton} ${showScrollTop ? styles.visible : ''}`}
            onClick={scrollToTop}
            aria-label="Scroll to top"
          >
            <ArrowUp size={15} />
          </button>
        </div>


      </main>
    </>
  );
}